import React from "react";
import { formatDate } from "admin/Authentication/hooks/useDateFormat";
import { IFeedbackJob, RecruitmentStatus } from "../types";

interface RecruitmentStatusScreenProps {
  job: IFeedbackJob;
  value: RecruitmentStatus | "";
  onValueChange: (value: RecruitmentStatus) => void;
  onContinue: (value: RecruitmentStatus) => void;
  onSkip: () => void;
}

const RecruitmentStatusScreen: React.FC<RecruitmentStatusScreenProps> = ({
  job,
  value,
  onContinue,
  onValueChange,
  onSkip,
}) => {
  const options: Array<{ value: RecruitmentStatus; label: string }> = [
    { value: "completed", label: "Yes, recruitment is complete" },
    { value: "in-progress", label: "No, still in progress" },
    { value: "cancelled", label: "Position no longer needed" },
  ];

  const getClosedExpiredDate = () => {
    if (job.closed_at) return formatDate(job.closed_at);
    if (job.expired === 1 || job.expired === "1" || job.expired === true) {
      return job.expiry_date ? formatDate(job.expiry_date) : "Expired";
    }
    return "N/A";
  };

  return (
    <div className="mb-6">
      <div className="flex items-center mb-2">
        <i className="ri-briefcase-line text-xl text-primary mr-2"></i>
        <span className="text-sm text-gray-500">
          Jobs <i className="ri-arrow-right-s-line mx-1"></i> Recruitment
          Feedback
        </span>
      </div>
      <div className="border rounded-xl bg-white p-6 mx-auto w-full">
        <div className="border rounded-lg p-6 mb-8 bg-gray-50">
          <div className="flex justify-between mb-4">
            <div className="flex-1">
              <p className="text-gray-500 text-sm">Job Title</p>
              <p className="font-medium text-lg">{job.title || "N/A"}</p>
            </div>
            <div className="flex-1">
              <p className="text-gray-500 text-sm">Posted</p>
              <p className="font-medium">{formatDate(job.created_at)}</p>
            </div>
            <div className="flex-1">
              <p className="text-gray-500 text-sm">Closed/Expired</p>
              <p className="font-medium">{getClosedExpiredDate()}</p>
            </div>
          </div>

          <div className="flex justify-between">
            <div className="flex-1">
              <p className="text-gray-500 text-sm">Applications</p>
              <p className="font-medium">
                {job.likes_count || "0"} candidates
              </p>
            </div>
            <div className="flex-1">
              <p className="text-gray-500 text-sm">Matched Talents</p>
              <p className="font-medium">
                {job.recommendations_count || "0"} talents
              </p>
            </div>
            <div className="flex-1"></div>
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Have you completed the recruitment process?
          </h2>

          <div className="space-y-3">
            {options.map((option) => (
              <label
                key={option.value}
                className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all ${
                  value === option.value
                    ? "border-green-500 bg-green-50"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="relative mr-3">
                  <input
                    type="radio"
                    name="recruitment-status"
                    checked={value === option.value}
                    onChange={() => onValueChange(option.value)}
                    className="sr-only"
                  />
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      value === option.value
                        ? "border-green-500"
                        : "border-gray-300"
                    }`}
                  >
                    {value === option.value && (
                      <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                    )}
                  </div>
                </div>
                <span className="text-gray-800">{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-between items-center pt-6 border-t">
          <button
            onClick={onSkip}
            className="px-6 py-2.5 text-gray-600 hover:text-gray-800 font-medium"
          >
            Skip for now
          </button>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => {
                if (value) {
                  onContinue(value as RecruitmentStatus);
                }
              }}
              disabled={!value}
              className={`px-6 py-2.5 rounded-lg font-medium ${
                value
                  ? "bg-primary text-white hover:bg-blue-700"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecruitmentStatusScreen;
